import { useEffect, useState } from 'react';

import QRCodeLib from 'qrcode';

/**
 * Renders `value` as a QR code at `size` pixels. Uses `qrcode`'s
 * `toString` API to emit raw SVG markup which we drop into the DOM via
 * `dangerouslySetInnerHTML` — there's no third-party React wrapper
 * pulled in, just the SVG string + a fallback while it's being
 * generated. Matches the contract the shared `LinkingPane` expects
 * for its `renderQrCode` prop.
 *
 * `qrcode.toString` is async so we render an empty placeholder of the
 * right size until the SVG resolves. The library cancels-and-restarts
 * on `value` change.
 */
export function QrRenderer({ value, size }: { value: string; size: number }): JSX.Element {
  const [svg, setSvg] = useState('');

  useEffect(() => {
    let cancelled = false;
    QRCodeLib.toString(value, { type: 'svg', margin: 0, width: size }, (err, str) => {
      if (err || cancelled) return;
      setSvg(str);
    });
    return () => {
      cancelled = true;
    };
  }, [value, size]);

  if (svg.length === 0) {
    return (
      <div
        style={{ width: size, height: size, background: '#F5F6F8' }}
        aria-label="Generating QR code…"
      />
    );
  }

  return (
    <div
      style={{ width: size, height: size }}
      aria-label="QR code"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
